/**
 * BICommandCenterCategoryNav — WQ-253.
 *
 * Two-row navigation for BI Command Center:
 *   - Top row: 6 category buttons (icon + label)
 *   - Bottom row: sub-tabs of the active category
 *
 * Pure presentational component. Parent owns state and click handlers.
 */

import React from 'react';
import { BI_CC_CATEGORIES } from './biCategoryRegistry';

interface BICommandCenterCategoryNavProps {
  activeCategory: string;
  activeSubTab: string;
  onCategoryChange: (categoryKey: string) => void;
  onSubTabChange: (subTabKey: string) => void;
}

const categoryRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: 8,
  flexWrap: 'wrap',
  padding: '8px 0',
  borderBottom: '1px solid var(--border-subtle, rgba(255,255,255,0.08))',
};

const subTabRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: 4,
  flexWrap: 'wrap',
  padding: '8px 0 16px',
};

const categoryButtonStyle = (active: boolean): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '8px 14px',
  borderRadius: 6,
  border: active
    ? '1px solid var(--accent-primary, #3b82f6)'
    : '1px solid var(--border-subtle, rgba(255,255,255,0.1))',
  background: active
    ? 'rgba(59, 130, 246, 0.15)'
    : 'transparent',
  color: active ? 'var(--accent-primary, #3b82f6)' : 'var(--text-primary)',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'all 0.15s',
});

const subTabButtonStyle = (active: boolean): React.CSSProperties => ({
  padding: '6px 12px',
  borderRadius: 4,
  border: 'none',
  borderBottom: active
    ? '2px solid var(--accent-primary, #3b82f6)'
    : '2px solid transparent',
  background: 'transparent',
  color: active
    ? 'var(--accent-primary, #3b82f6)'
    : 'var(--text-secondary)',
  fontSize: 13,
  fontWeight: active ? 600 : 500,
  cursor: 'pointer',
  transition: 'all 0.15s',
});

const BICommandCenterCategoryNav: React.FC<BICommandCenterCategoryNavProps> = ({
  activeCategory,
  activeSubTab,
  onCategoryChange,
  onSubTabChange,
}) => {
  const currentCategory = BI_CC_CATEGORIES.find((c) => c.key === activeCategory);

  return (
    <nav data-testid="bi-cc-nav">
      {/* Top row — categories */}
      <div role="tablist" aria-label="BI Command Center categories" data-testid="bi-cc-categories" style={categoryRowStyle}>
        {BI_CC_CATEGORIES.map((cat) => {
          const Icon = cat.icon;
          const isActive = cat.key === activeCategory;
          return (
            <button
              key={cat.key}
              role="tab"
              type="button"
              data-testid={`bi-cc-category-${cat.key}`}
              aria-selected={isActive}
              aria-controls={`bi-cc-sub-tabs-${cat.key}`}
              onClick={() => onCategoryChange(cat.key)}
              style={categoryButtonStyle(isActive)}
            >
              <Icon size={16} />
              {cat.label}
            </button>
          );
        })}
      </div>

      {/* Bottom row — sub-tabs of the active category */}
      {currentCategory && (
        <div
          role="tablist"
          aria-label={`${currentCategory.label} sub-tabs`}
          data-testid="bi-cc-sub-tabs"
          id={`bi-cc-sub-tabs-${currentCategory.key}`}
          style={subTabRowStyle}
        >
          {currentCategory.subTabs.map((sub) => {
            const isActive = sub.key === activeSubTab;
            return (
              <button
                key={sub.key}
                role="tab"
                type="button"
                data-testid={`bi-cc-sub-tab-${sub.key}`}
                aria-selected={isActive}
                onClick={() => onSubTabChange(sub.key)}
                style={subTabButtonStyle(isActive)}
              >
                {sub.label}
              </button>
            );
          })}
        </div>
      )}
    </nav>
  );
};

export default BICommandCenterCategoryNav;
